import styled from "styled-components";
import { useSelector } from "react-redux";
import { AuthUserContext } from "../../context/AuthUserContext";
import { useContext, useState } from "react";
import { useNavigation } from "../../utils/navigation";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

const Container = styled.div`
  position: relative;
`;

const MainHeaderWrapper = styled.div`
  width: 100%;

  max-width: 100vw;
  z-index: var(--zindex-top);
  position: fixed;
  top: ${(props) => (props.$isScrolled ? "0" : "initial")};
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  background-color: var(--bg-100);
  border-bottom: 1px solid var(--bg-300);

  box-shadow: ${(props) =>
    props.$isScrolled ? "var(--shadow-medium)" : "none"};

  transition: ${(props) =>
    props.$isScrolled ? "all 0.2s ease-out" : "all 0.1s ease-in"};
`;
const GridContainer = styled.header`
  height: var(--navbar-height);
  width: 100%;
  max-width: 90%;

  display: grid;
  grid-template-columns: 1fr 1fr;

  @media (min-width: 768px) {
    height: var(--navbar-height-desktop);
    grid-template-columns: 1fr 1fr;
    max-width: var(--max-width-container);
    padding: 0;
  }
  @media (min-width: 768px) and (max-width: 1040px) {
    max-width: 90%;
  }
`;
const GridItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: var(--spacing-sm);

  @media (min-width: 768px) {
    gap: var(--spacing-md);
  }

  &:nth-child(2) {
    justify-content: flex-end; /* Drugi element postaje prvi */
  }
`;

const LogoImg = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;

  svg {
    width: 36px;
    height: 36px;
    @media (min-width: 768px) {
      width: 48px;
      height: 48px;
    }
  }
`;

const HeaderTitle = styled.h2`
  margin: 0;
  font-size: var(--font-size-large);
  font-weight: 600;
  letter-spacing: -0.01em;
  color: var(--text-100);

  @media (min-width: 768px) {
    font-size: var(--font-size-xxlarge);
  }
`;

const BackLink = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  cursor: pointer;
  padding: var(--spacing-xxs) var(--spacing-xs);
  border-radius: var(--border-radius-base);
  transition: background-color var(--transition-fast);

  svg {
    width: 22px;
    height: 22px;
    flex-shrink: 0;
    transition: transform var(--transition-fast);
    @media (min-width: 768px) {
      width: 26px;
      height: 26px;
    }
  }

  &:hover {
    background-color: var(--bg-200);
  }
  &:hover svg {
    transform: translateX(-2px);
  }
`;

const HeaderSubtitle = styled.h4`
  margin: 0;
  font-size: var(--font-size-small);
  font-weight: 500;
  color: var(--text-200);
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);

  @media (min-width: 768px) {
    font-size: var(--font-size-base);
  }
`;

const CartCountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: var(--border-radius-large);
  background-color: var(--primary-100);
  color: var(--bg-100);
  font-size: var(--font-size-small);
  font-weight: 600;
`;

const CheckoutHeader = () => {
  const { t } = useTranslation();
  const [logoHovered, setLogoHovered] = useState(false);
  const { authTokens } = useContext(AuthUserContext);
  const { goToHome } = useNavigation();
  const navigate = useNavigate();
  const cartItems = useSelector((state) => state.cart.itemsList);
  const totalQuantity = cartItems.reduce(
    (total, item) => total + item.quantity,
    0
  );
  return (
    <Container>
      <MainHeaderWrapper>
        <GridContainer>
          <GridItem>
            <LogoImg
              onClick={() => navigate(goToHome())}
              role="button"
              tabIndex={0}
              aria-label={t("ARIA.HOME")}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  navigate(goToHome());
                }
              }}
            >
              <svg
                width="50"
                height="50"
                viewBox="0 0 298 298"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                onMouseEnter={() => setLogoHovered(true)}
                onMouseLeave={() => setLogoHovered(false)}
              >
                <path
                  d="M148.707 0C66.7245 0 0 66.7245 0 148.707C0 230.689 66.7245 297.414 148.707 297.414C230.689 297.414 297.414 230.689 297.414 148.707C297.414 66.7245 230.689 0 148.707 0ZM294.707 148.707C294.707 188.275 278.843 224.204 253.18 250.567L244.688 242.074C268.158 217.904 282.669 184.962 282.669 148.707C282.669 112.452 267.878 78.8562 243.988 54.6394L252.48 46.1472C278.563 72.557 294.661 108.766 294.661 148.707H294.707ZM17.3576 148.707C17.3576 76.2899 76.2432 17.4043 148.66 17.4043C221.077 17.4043 279.963 76.2899 279.963 148.707C279.963 221.124 221.077 280.009 148.66 280.009C76.2432 280.009 17.3576 221.124 17.3576 148.707ZM250.52 44.2808L242.028 52.7731C217.858 29.2562 184.915 14.7447 148.614 14.7447C112.312 14.7447 79.416 29.2562 55.2458 52.7731L46.7536 44.2808C73.0701 18.6176 108.999 2.75295 148.567 2.75295C188.135 2.75295 224.11 18.6175 250.427 44.3275L250.52 44.2808ZM44.7939 46.1939L53.2862 54.6862C29.3494 78.9029 14.558 112.172 14.558 148.8C14.558 185.429 29.0693 218.044 52.5862 242.214L44.1406 250.707C18.4307 224.39 2.56627 188.415 2.56627 148.847C2.56627 109.279 18.7107 72.6504 44.7939 46.2872V46.1939ZM46.0539 252.667L54.5461 244.174C78.7629 268.064 111.985 282.856 148.614 282.856C185.242 282.856 218.511 268.064 242.728 244.128L251.22 252.62C224.857 278.703 188.602 294.848 148.66 294.848C108.719 294.848 72.5102 278.703 46.147 252.667"
                  fill="var(--text-200)"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M207.722 57.7351C202.622 66.9627 200.922 78.8759 205.416 87.3302C214.54 104.491 230.158 114.314 239.894 131.851C273.15 191.781 209.011 201.72 174.784 209.915C155.428 214.537 143.812 230.506 158.492 258.688C214.436 253.98 258.39 207.07 258.39 149.903C258.39 111.144 238.177 77.1018 207.722 57.7266"
                  fill={
                    !logoHovered ? "var(--primary-200)" : "var(--primary-100)"
                  }
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M191.927 105.506C177.373 95.2564 162.525 82.7355 160.972 63.8935C160.307 55.8558 164.875 48.4915 171.632 43.0459C157.459 40.0907 142.84 39.9776 128.623 42.713C114.406 45.4483 100.873 50.9779 88.8086 58.9812C96.9207 91.2874 127.561 94.6763 155.629 103.143C195.722 115.235 214.322 153.656 158.871 159.818C81.4401 168.423 72.2954 187.545 127.861 256.987C133.447 258.095 139.112 258.763 144.803 258.984C114.222 212.039 131.18 198.9 188.95 180.965C245.166 163.485 224.646 128.565 191.925 105.51"
                  fill="var(--accent-100)"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M123.936 118.509C100.162 114.213 67.312 112.348 58.2119 97.5595C57.3549 96.166 56.5939 94.7157 55.9342 93.2188C45.5264 110.296 40.0342 129.915 40.0626 149.915C40.0626 197.952 71.1131 238.761 114.236 253.341C95.0693 233.567 63.4812 210.21 71.1047 172.043C76.204 146.547 105.038 150.269 127.675 148.404C160.298 145.722 156.82 124.463 123.927 118.509"
                  fill={
                    !logoHovered ? "var(--primary-200)" : "var(--primary-100)"
                  }
                />
              </svg>
            </LogoImg>
            <HeaderTitle>{t("CHECKOUT_HEADER.TITLE")}</HeaderTitle>
          </GridItem>
          <GridItem>
            <BackLink
              onClick={() => navigate(-1)}
              role="button"
              tabIndex={0}
              aria-label={t("ARIA.BACK")}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  navigate(-1);
                }
              }}
            >
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M7.68473 7.33186C8.07526 6.94134 8.07526 6.30817 7.68473 5.91765C7.29421 5.52712 6.66105 5.52712 6.27052 5.91765L1.60492 10.5832C0.823873 11.3643 0.823872 12.6306 1.60492 13.4117L6.27336 18.0801C6.66388 18.4706 7.29705 18.4706 7.68757 18.0801C8.0781 17.6896 8.0781 17.0564 7.68757 16.6659L4.02154 12.9998L22 12.9998C22.5523 12.9998 23 12.5521 23 11.9998C23 11.4476 22.5523 10.9998 22 10.9998L4.01675 10.9998L7.68473 7.33186Z"
                  fill="var(--text-200)"
                />
              </svg>
              <HeaderSubtitle>
                {t("CHECKOUT_HEADER.SUBTITLE")}
                {totalQuantity > 0 && (
                  <CartCountBadge>{totalQuantity}</CartCountBadge>
                )}
              </HeaderSubtitle>
            </BackLink>
          </GridItem>
        </GridContainer>
      </MainHeaderWrapper>
    </Container>
  );
};

export default CheckoutHeader;
